import React from 'react';
import { GlobeAltIcon, Squares2X2Icon } from '@heroicons/react/24/outline';
import { getTranscriptionLanguageName } from '../../constants/transcriptionLanguages';
import { DESTINATION_INFO, DIGWATCH_API_VERSION_INFO, REPORT_SECTIONS } from './publishOptions';

const choiceClass = (selected) => `border-2 transition-all ${
  selected
    ? 'border-blue-600 bg-blue-50 text-blue-700'
    : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
} disabled:opacity-50 disabled:cursor-not-allowed`;

const checkRowClass = (checked, disabled, layout) => `flex ${layout} gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
  checked
    ? 'border-indigo-300 bg-indigo-50'
    : 'border-gray-200 bg-white hover:border-indigo-200'
} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

const languageLabel = (lang) => lang.name || getTranscriptionLanguageName(lang.code);

export function DestinationChoice({ destinations, destination, onChange, disabled }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Publish destination
      </label>
      <div className="flex gap-2">
        {destinations.map((dest) => (
          <button
            key={dest}
            type="button"
            onClick={() => onChange(dest)}
            disabled={disabled}
            className={`flex-1 px-4 py-2.5 text-sm font-medium rounded-lg ${choiceClass(dest === destination)}`}
          >
            {DESTINATION_INFO[dest]?.name || dest}
          </button>
        ))}
      </div>
    </div>
  );
}

export function ApiVersionChoice({ version, onChange, disabled }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        DigWatch API version
      </label>
      <div className="flex flex-col gap-2">
        {Object.entries(DIGWATCH_API_VERSION_INFO).map(([key, meta]) => (
          <button
            key={key}
            type="button"
            onClick={() => onChange(key)}
            disabled={disabled}
            className={`w-full px-4 py-3 text-left rounded-lg ${choiceClass(key === version)}`}
          >
            <div className="text-sm font-medium">{meta.label}</div>
            <div className="text-xs mt-0.5 opacity-80">{meta.description}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function SectionPicker({ sections, onToggle, destinationName, disabled }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Squares2X2Icon className="h-5 w-5 text-gray-600" />
        <h3 className="text-sm font-semibold text-gray-900">Sections to send</h3>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        The base session info is always sent. Pick which additional sections to publish to {destinationName}.
      </p>
      <div className="flex flex-col gap-2">
        {REPORT_SECTIONS.map((section) => (
          <label key={section.key} className={checkRowClass(sections[section.key], disabled, 'items-start')}>
            <input
              type="checkbox"
              checked={!!sections[section.key]}
              onChange={() => onToggle(section.key)}
              disabled={disabled}
              className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-800">
              <span className="font-medium">{section.label}</span>
              <span className="block text-xs text-gray-500">{section.description}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

const NOTICE_TONES = {
  info: { box: 'bg-blue-50 border-blue-200', icon: 'text-blue-600', title: 'text-blue-900', text: 'text-blue-700' },
  warning: { box: 'bg-amber-50 border-amber-200', icon: 'text-amber-600', title: 'text-amber-900', text: 'text-amber-700' },
};

export function Notice({ tone, Icon, title, children }) {
  const colors = NOTICE_TONES[tone];
  return (
    <div className={`${colors.box} border rounded-lg p-4`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <Icon className={`h-6 w-6 ${colors.icon}`} />
        </div>
        <div className="flex-1">
          <h3 className={`text-sm font-semibold ${colors.title} mb-1`}>
            {title}
          </h3>
          <p className={`text-sm ${colors.text}`}>{children}</p>
        </div>
      </div>
    </div>
  );
}

export function LanguagePicker({ languages, selected, onToggle, onSelectAll, onClear, destinationName, disabled }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <GlobeAltIcon className="h-5 w-5 text-gray-600" />
          <h3 className="text-sm font-semibold text-gray-900">Select Languages</h3>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={onSelectAll}
            disabled={disabled}
            className="text-indigo-600 hover:text-indigo-800 disabled:opacity-50"
          >
            Select all
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
          >
            Clear
          </button>
        </div>
      </div>
      <p className="text-xs text-gray-600 mb-3">
        Choose one or more language versions to publish to {destinationName}.
      </p>
      <div className="flex flex-col gap-2">
        {languages.map((lang) => (
          <label key={lang.code} className={checkRowClass(selected.includes(lang.code), disabled, 'items-center')}>
            <input
              type="checkbox"
              checked={selected.includes(lang.code)}
              onChange={() => onToggle(lang.code)}
              disabled={disabled}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-800">
              {languageLabel(lang)}
              <span className="text-gray-400 ml-1">({lang.code.toUpperCase()})</span>
            </span>
          </label>
        ))}
      </div>
      {selected.length === 0 && (
        <p className="text-xs text-amber-600 mt-2">Select at least one language to publish.</p>
      )}
    </div>
  );
}

export function PublishingSpinner() {
  return (
    <span className="flex items-center gap-2">
      <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Publishing...
    </span>
  );
}
